// Classify a `GET /api/series/:name` response so the UI can tell three genuinely
// different outcomes apart.
//
// This exists because the series fetch used to collapse two of them. The old check
// was `if (!data.points || !data.points.length) return;` — a silent early return
// that fired for BOTH a sparse-but-valid 200 and a hard 404. The sparse case is
// deliberate (an FRED series with few points should keep its chip so the user can
// remove it), but routing a 404 down the same path meant an UNAVAILABLE signal
// rendered as a normal selected/plotted chip with no line and no explanation.
//
// That is reachable on the reload path: a suggestion saved to the signal library
// persists `{label, query, ssid}` in localStorage, and a later click 404s whenever
// the ssid no longer resolves — the signal was delisted, the catalog re-derived its
// ids, or the signals-service isn't configured for this deployment (in which case
// the route falls through to name resolution, and an agent-authored display label
// like "NVDA Data Center Revenue" is not a resolvable legacy series name).
//
// Pure and total — no fetch, no React. The hook does the I/O and applies the verdict.

/** A 404 means the server positively knows it cannot serve this series. */
const NOT_FOUND = 404;

/**
 * @typedef {object} SeriesVerdict
 * @property {"data"|"sparse"|"unavailable"} kind
 *   `data`        — usable points arrived; plot them.
 *   `sparse`      — a valid 200 carrying no points (a real but too-thin series).
 *                   Keep the chip as-is; this is the pre-existing deliberate
 *                   behavior and is NOT an error.
 *   `unavailable` — the server cannot serve this series at all. Surface it.
 * @property {string|null} reason  Short user-facing explanation (null unless unavailable).
 * @property {boolean} transient   True when a retry could plausibly succeed
 *                                 (unreachable source) rather than the signal being
 *                                 genuinely gone. Lets the UI word it honestly and
 *                                 avoids branding a blip as permanent removal.
 */

/**
 * Classify a series response.
 *
 * @param {{ok: boolean, status: number, body: object|null}} response
 *   `ok`/`status` from the Response; `body` the parsed JSON (null when unparseable).
 * @returns {SeriesVerdict}
 */
export function classifySeriesResponse({ ok, status, body } = {}) {
  if (!ok) {
    if (status === NOT_FOUND) {
      return {
        kind: "unavailable",
        reason: "No longer available from the signal source.",
        transient: false,
      };
    }
    return {
      kind: "unavailable",
      reason: "Couldn't load — the signal source is unreachable.",
      transient: true,
    };
  }
  const points = body && Array.isArray(body.points) ? body.points : null;
  if (!points || !points.length) return { kind: "sparse", reason: null, transient: false };
  return { kind: "data", reason: null, transient: false };
}

/**
 * The verdict for a fetch that never produced a response (network error / offline).
 * Always transient — we learned nothing about the signal itself.
 *
 * @returns {SeriesVerdict}
 */
export function networkFailureVerdict() {
  return {
    kind: "unavailable",
    reason: "Couldn't load — check your connection.",
    transient: true,
  };
}
